import React, { useState, useEffect, useRef } from 'react';
import { Menu, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Section {
  id: string;
  title: string;
}

const sections: Section[] = [
  { id: 'overview', title: 'Overview' },
  { id: 'problem', title: 'Problem & Insight' },
  { id: 'solution', title: 'Solution & Process' },
  { id: 'results', title: 'Results & Impact' }
];

function SkillzStore() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-100px 0px -60% 0px',
        threshold: [0, 0.3, 0.5, 0.7, 1]
      }
    );

    sections.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-black">
      <div className="w-full h-[224px] md:h-[280px] lg:h-[336px] overflow-hidden">
        <img
          src="/skillzlogo.png"
          alt="Skillz Store"
          className="w-full h-full object-cover"
        />
      </div>

      <div className="flex relative">
        {isSidebarOpen && (
          <aside className="sticky top-0 h-screen w-80 bg-zinc-950 border-r border-white/10 overflow-y-auto">
            <div className="p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-2">
                  <Link
                    to="/"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Home size={18} />
                  </Link>
                  <button
                    onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-800 text-white/80 hover:bg-zinc-700 hover:text-white transition-all"
                  >
                    <Menu size={18} />
                  </button>
                </div>
                <div className="text-white text-sm">Skillz Store</div>
                <nav className="space-y-1">
                  {sections.map(({ id, title }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className={`w-full text-left px-3 py-2 text-sm transition-all ${
                        activeSection === id
                          ? 'text-blue-500 border-l-2 border-blue-500'
                          : 'text-white/60 hover:text-white border-l-2 border-transparent'
                      }`}
                    >
                      {title}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </aside>
        )}

        {!isSidebarOpen && (
          <div className="sticky top-0 h-fit pt-6 pl-6">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-zinc-900/90 backdrop-blur-xl text-white/80 hover:bg-zinc-800 hover:text-white transition-all border border-white/10"
            >
              <Menu size={18} />
            </button>
          </div>
        )}

        <main className={`flex-1 max-w-6xl p-4 py-20 transition-all duration-300 ${isSidebarOpen ? 'ml-auto mr-32' : 'mx-auto'}`}>
          <div className="space-y-20">

            <section id="overview" className="space-y-6 scroll-mt-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Overview</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Skillz is the leading mobile gaming platform that connects players and developers through fun and fair competition. 
                </p>
                <p>
                  We redesigned Skillz's deposit store experience across multiple features to solve a fundamental problem: our outdated interface wasn't converting users or showcasing our offers effectively. Leading the execution across 4 major features, our team drove incremental annual revenue.
                </p>
              </div>
            </section>

            <section id="problem" className="space-y-6 scroll-mt-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Problem & Insight</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  The existing deposit store had a basic, outdated design that failed to engage users. Limited Time Offers (LTOs) had poor visibility, and the overall experience lacked the modern feel needed to drive conversions in a competitive mobile gaming market.
                </p>
                <p className="font-medium text-white/90">Business Impact:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Users weren't depositing as much as they could</li>
                  <li>Offers were buried and hard to discover</li>
                  <li>The store felt dated compared to competitors</li>
                </ul>
                <p className="font-medium text-white/90 pt-4">What I Found:</p>
                <p>
                  Through competitive analysis of 15+ platforms, I identified three key issues:
                </p>
                <ol className="list-decimal list-inside space-y-2 pl-4">
                  <li><strong className="text-white/90">Poor offer visibility</strong> - LTOs were shown as small, static tiles that didn't communicate value</li>
                  <li><strong className="text-white/90">Lack of engagement mechanics</strong> - No animations, no excitement, no reason to deposit <em>now</em></li>
                  <li><strong className="text-white/90">Missed monetization opportunities</strong> - No upsells, no strategic nudges, no promo mechanics</li>
                </ol>
                <p>
                  I analyzed hundreds of competitor screenshots to understand what makes deposit experiences work - when offers appear, how they're positioned, what incentives drive action.
                </p>
              </div>
            </section>

            <div className="w-full space-y-2">
              <h3 className="text-2xl text-white font-serif text-center">Impact & Work</h3>
              <img
                src="/problems.png"
                alt="Skillz Store Problems"
                className="w-full max-w-2xl mx-auto rounded-2xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-8 items-center">
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p className="font-medium text-white/90 text-lg">Skillz Store</p>
                <p>
                  Revamped Store UI to boost user deposits through modern design.
                </p>
                <p>
                  Redesigned Skillz's deposit store to fix a critical problem - our store looked outdated and wasn't converting users. Through competitive analysis of 15+ gaming platforms, I identified that offers had poor visibility, lacked engagement mechanics, and missed monetization opportunities.
                </p>
                <p>
                  I led the end-to-end redesign across 4 core features: LTO carousel redesign, smart offer nudges, deposit success screen upsells, and strategic payment flow improvements. Coordinated with engineering, design, analytics, and marketing teams to deliver impact.
                </p>
              </div>
              <div className="flex justify-center">
                <img
                  src="/bothimages.png"
                  alt="Skillz Store Before and After"
                  className="w-full max-w-md rounded-2xl"
                />
              </div>
            </div>

            <section id="solution" className="scroll-mt-20 space-y-8">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Solution & Process</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  Led the end-to-end redesign across two major milestones, working with engineering, design, analytics, and marketing teams to deliver 4 core features.
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10" style={{ borderWidth: '1px' }}>
                  <thead>
                    <tr className="border-b border-white/20">
                      <th className="text-left py-4 px-4 text-white font-medium">Feature</th>
                      <th className="text-left py-4 px-4 text-white font-medium">Problem</th>
                      <th className="text-left py-4 px-4 text-white font-medium">Solution</th>
                      <th className="text-left py-4 px-4 text-white font-medium">Why This Worked</th>
                    </tr>
                  </thead>
                  <tbody className="text-white/70 text-sm leading-relaxed">
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4 align-top font-medium text-white/90">LTO Carousel Redesign</td>
                      <td className="py-4 px-4 align-top">LTOs were buried in small tiles. Users had to click each one to see offer details.</td>
                      <td className="py-4 px-4 align-top">Designed an auto-rotating carousel showcasing full offer value upfront (real cash + bonus cash + Z's + tickets). Rotates every 4-5 seconds, users can also swipe manually.</td>
                      <td className="py-4 px-4 align-top">After launch, we noticed purchases dropped. Realized users didn't know to scroll. Proposed auto-rotation feature - once implemented, engagement recovered and exceeded targets.</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4 align-top font-medium text-white/90">Smart Offer Nudges</td>
                      <td className="py-4 px-4 align-top">Offers weren't appearing at high-intent moments.</td>
                      <td className="py-4 px-4 align-top">Introduced contextual nudges: after 5 losses → high-value offer; when balance hits $0 → deposit prompt; strategic re-pops based on behavior.</td>
                      <td className="py-4 px-4 align-top">Captured users at moments when they were most likely to convert, improving deposit rates.</td>
                    </tr>
                    <tr className="border-b border-white/10">
                      <td className="py-4 px-4 align-top font-medium text-white/90">Deposit Success Screen</td>
                      <td className="py-4 px-4 align-top">Post-deposit experience was boring and transactional.</td>
                      <td className="py-4 px-4 align-top">Created celebratory screen with animations + upsell opportunity (discounted game templates).</td>
                      <td className="py-4 px-4 align-top">XX% click-through rate on discounted templates. Directly increased GMV.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="space-y-4 text-white/70 leading-relaxed pt-6">
                <p className="font-medium text-white/90 text-lg">My Process</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>
                    <strong className="text-white/90">Research & Analysis</strong> - Conducted competitive analysis across 15+ gaming apps to identify monetization best practices
                  </li>
                  <li>
                    <strong className="text-white/90">Strategy & Design</strong> - Created wireframes and worked with designers to translate concepts into polished UI
                  </li>
                  <li>
                    <strong className="text-white/90">Execution & Launch</strong> - Led launch independently and coordinated with QA for testing, worked with PMM on GTM strategy
                  </li>
                  <li>
                    <strong className="text-white/90">Post-Launch Optimization</strong> - Monitored dashboards, conducted user interviews, ran A/B tests and iterated based on data
                  </li>
                </ul>
              </div>
            </section>

            <section id="results" className="space-y-6 scroll-mt-20 pb-20">
              <h2 className="text-3xl md:text-4xl text-white font-serif">Results & Impact</h2>
              <div className="space-y-4 text-white/70 leading-relaxed">
                <p>
                  The store revamp exceeded expectations across every key metric:
                </p>
                <p className="font-medium text-white/90">Revenue & Engagement:</p>
                <ul className="list-disc list-inside space-y-2 pl-4">
                  <li>Total deposits improved</li>
                  <li>Net deposits showed strong growth</li>
                  <li>Install-to-deposit rate significantly higher than control group</li>
                  <li>Deposits through LTOs and deposit slates both increased</li>
                  <li>Generated incremental annual revenue</li>
                </ul>
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default SkillzStore;
